import { useCallback, useEffect, useState } from 'react';
import axios from '../../utils/axios';
import { API_URL } from '../../constants';
import {
  FulfillmentLifecycleStatus,
  FulfillmentLineExceptionType,
  FulfillmentLineStatus,
  LineItemLocalState,
  OrderDetail,
} from '../../services/fulfillment/types';
import { buildInitialLineStatuses, resolveQcZoneTypes } from './FulfillmentOrderDetail.utils';

export interface SaveLinePayload {
  status: FulfillmentLineStatus;
  fulfillmentBinCode?: string;
  exceptionType?: FulfillmentLineExceptionType;
  notes?: string;
}

export interface UseFulfillmentOrderResult {
  order: OrderDetail | null;
  loading: boolean;
  error: string | null;
  orderStatus: FulfillmentLifecycleStatus | null;
  savingOrderStatus: boolean;
  orderStatusWarning: string | null;
  lineStatuses: Record<string, LineItemLocalState>;
  savingLineGid: string | null;
  lineBinWarnings: Record<string, string>;
  apiError: string | null;
  qcZoneTypes: string[];
  handleOrderStatusChange: (newStatus: FulfillmentLifecycleStatus) => Promise<void>;
  handleLineStatusSelectChange: (gid: string, newStatus: FulfillmentLineStatus) => void;
  patchLineLocalState: (gid: string, patch: Partial<LineItemLocalState>) => void;
  saveLineStatus: (gid: string, payload: SaveLinePayload) => Promise<void>;
  refresh: () => void;
  clearApiError: () => void;
  clearOrderStatusWarning: () => void;
  clearLineBinWarning: (gid: string) => void;
}

export function useFulfillmentOrder(shopifyOrderId: string | undefined): UseFulfillmentOrderResult {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<FulfillmentLifecycleStatus | null>(null);
  const [savingOrderStatus, setSavingOrderStatus] = useState(false);
  const [orderStatusWarning, setOrderStatusWarning] = useState<string | null>(null);
  const [lineStatuses, setLineStatuses] = useState<Record<string, LineItemLocalState>>({});
  const [savingLineGid, setSavingLineGid] = useState<string | null>(null);
  const [lineBinWarnings, setLineBinWarnings] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [qcZoneTypes, setQcZoneTypes] = useState<string[]>(['qc']);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!shopifyOrderId) return;
    let cancelled = false;
    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const orderRes = await axios.get(`${API_URL}/fulfillment/orders/${shopifyOrderId}`);
        if (cancelled) return;
        const orderData: OrderDetail = (orderRes as any).data;
        setOrderStatus((orderData.lifecycleStatus as FulfillmentLifecycleStatus) ?? null);
        setLineStatuses(buildInitialLineStatuses(orderData));
        setOrder(orderData);
        setQcZoneTypes(resolveQcZoneTypes(orderData));
      } catch {
        if (!cancelled) setError('Failed to load order details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOrder();
    return () => {
      cancelled = true;
    };
  }, [shopifyOrderId, refreshKey]);

  const handleOrderStatusChange = useCallback(
    async (newStatus: FulfillmentLifecycleStatus) => {
      const prev = orderStatus;
      setOrderStatus(newStatus);
      setSavingOrderStatus(true);
      try {
        const res = await axios.patch(`${API_URL}/fulfillment/orders/${shopifyOrderId}/status`, {
          status: newStatus,
        });
        const warning = (res as any).data?.warning;
        if (warning) setOrderStatusWarning(warning);
      } catch {
        setOrderStatus(prev);
        setApiError('Failed to update order status');
      } finally {
        setSavingOrderStatus(false);
      }
    },
    [orderStatus, shopifyOrderId],
  );

  const saveLineStatus = useCallback(
    async (gid: string, payload: SaveLinePayload) => {
      const prev = lineStatuses[gid];
      setSavingLineGid(gid);
      setLineBinWarnings(w => ({ ...w, [gid]: '' }));
      try {
        const res = await axios.patch(
          `${API_URL}/fulfillment/orders/${shopifyOrderId}/lines/status`,
          { lineItemGid: gid, ...payload },
        );
        const warning = (res as any).data?.warning;
        setLineStatuses(s => ({
          ...s,
          [gid]: {
            status: payload.status,
            fulfillmentBinCode: payload.fulfillmentBinCode ?? null,
            exceptionType: payload.exceptionType ?? null,
            notes: payload.notes ?? null,
            pendingStatus: null,
            pendingBin: null,
            pendingBinTypeahead: '',
            pendingExceptionType: null,
            pendingNotes: null,
          },
        }));
        if (warning) setLineBinWarnings(w => ({ ...w, [gid]: warning }));
      } catch {
        setLineStatuses(s => ({ ...s, [gid]: prev }));
        setApiError('Failed to update line item status');
      } finally {
        setSavingLineGid(null);
      }
    },
    [lineStatuses, shopifyOrderId],
  );

  const patchLineLocalState = useCallback((gid: string, patch: Partial<LineItemLocalState>) => {
    setLineStatuses(prev => ({ ...prev, [gid]: { ...prev[gid], ...patch } }));
  }, []);

  const handleLineStatusSelectChange = useCallback(
    (gid: string, newStatus: FulfillmentLineStatus) => {
      if (newStatus === 'pending') {
        saveLineStatus(gid, { status: newStatus });
      } else {
        setLineStatuses(prev => ({
          ...prev,
          [gid]: {
            ...prev[gid],
            pendingStatus: newStatus,
            pendingBin: null,
            pendingBinTypeahead: '',
            pendingExceptionType: null,
            pendingNotes: null,
          },
        }));
      }
    },
    [saveLineStatus],
  );

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);
  const clearApiError = useCallback(() => setApiError(null), []);
  const clearOrderStatusWarning = useCallback(() => setOrderStatusWarning(null), []);
  const clearLineBinWarning = useCallback(
    (gid: string) => setLineBinWarnings(w => ({ ...w, [gid]: '' })),
    [],
  );

  return {
    order,
    loading,
    error,
    orderStatus,
    savingOrderStatus,
    orderStatusWarning,
    lineStatuses,
    savingLineGid,
    lineBinWarnings,
    apiError,
    qcZoneTypes,
    handleOrderStatusChange,
    handleLineStatusSelectChange,
    patchLineLocalState,
    saveLineStatus,
    refresh,
    clearApiError,
    clearOrderStatusWarning,
    clearLineBinWarning,
  };
}
